<script setup lang="ts">
import {
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardRoot,
  HoverCardTrigger,
} from 'reka-ui'

defineOptions({ name: 'SourceToolHoverCard' })

const props = withDefaults(
  defineProps<{
    title: string
    description?: string
    openDelayMs?: number
    sideOffsetPx?: number
    side?: 'top' | 'right' | 'bottom' | 'left'
    compact?: boolean
    disabled?: boolean
    /**
     * Si true, le panneau hover reste fermé (ex. autre surface ouverte sur le même trigger).
     * Garde le HoverCard monté pour des clics fiables sur le trigger.
     */
    hoverContentBlocked?: boolean
  }>(),
  {
    description: '',
    openDelayMs: 250,
    sideOffsetPx: 10,
    side: 'bottom',
    compact: false,
    disabled: false,
    hoverContentBlocked: false,
  },
)

const hoverState = ref(false)

watch(
  () => props.hoverContentBlocked,
  (blocked) => {
    if (blocked) {
      hoverState.value = false
    }
  },
)

const hoverCardOpenBinding = computed({
  get() {
    return props.hoverContentBlocked ? false : hoverState.value
  },
  set(nextOpen: boolean) {
    if (props.hoverContentBlocked) {
      hoverState.value = false
      return
    }
    hoverState.value = nextOpen
  },
})

const hoverDisabled = computed(function computeHoverDisabled() {
  return props.disabled || props.hoverContentBlocked
})
</script>

<template>
  <span class="source-tool-hover-card-root">
    <slot v-if="hoverDisabled" name="trigger" />
    <HoverCardRoot v-else v-model:open="hoverCardOpenBinding" :open-delay="openDelayMs">
      <!--
        Enveloppe sous le trigger : les <button disabled> ne reçoivent pas le pointer (pas de survol).
        pointer-events: none sur le bouton désactivé laisse le span recevoir pointerenter / ouverture HoverCard.
      -->
      <HoverCardTrigger as-child>
        <span class="source-tool-hover-card-trigger">
          <slot name="trigger" />
        </span>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          class="source-tool-hover-card"
          :class="{ 'source-tool-hover-card--compact': compact }"
          :side="side"
          :side-offset="sideOffsetPx"
        >
          <HoverCardArrow class="source-tool-hover-card__arrow" />
          <div class="source-tool-hover-card__body">
            <div class="source-tool-hover-card__title">{{ title }}</div>
            <p v-if="description" class="source-tool-hover-card__desc">{{ description }}</p>
            <div v-if="$slots.shortcut" class="source-tool-hover-card__shortcut">
              <slot name="shortcut" />
            </div>
          </div>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCardRoot>
  </span>
</template>

<style lang="scss">
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/partials/elpdl/colors" as c;

/* Conteneur explicite : hit-test fiable sous un parent en pointer-events: none (ex. boîte à outils). */
.source-tool-hover-card-root {
  display: inline-flex;
  pointer-events: auto;
}

.source-tool-hover-card-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  pointer-events: auto;
}

.source-tool-hover-card-trigger button:disabled {
  pointer-events: none;
}

.source-tool-hover-card {
  z-index: 12050;
  max-width: 340px;
  min-width: 220px;
  padding: $spacing-m $spacing-l;
  border-radius: 12px;
  background: var(--color-bg-elevated) !important;
  box-shadow: var(--color-shadow-card);
  border: 1px solid var(--color-border-subtle);
  position: relative;
}

.source-tool-hover-card__arrow {
  fill: var(--color-bg-elevated) !important;
  stroke: var(--color-border-subtle);
  stroke-width: 1px;
}

.source-tool-hover-card__body {
  display: flex;
  flex-direction: column;
  gap: $spacing-s;
}

.source-tool-hover-card__title {
  font-weight: 800;
  font-size: 0.95rem;
  color: var(--color-text);
  line-height: 1.25;
}

.source-tool-hover-card__desc {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--color-text-secondary);
  line-height: 1.45;
}

.source-tool-hover-card--compact {
  min-width: 0;
  max-width: none;
  padding: $spacing-2xs $spacing-xs;
  border-radius: 4px;
}

.source-tool-hover-card--compact .source-tool-hover-card__title {
  font-family: 'Public Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
}

.source-tool-hover-card__shortcut {
  display: inline-flex;
  align-items: center;
  gap: $spacing-xs;
  color: c.$enedis-neutral-700;
}
</style>

